/**
 * OAuth Service
 *
 * Handles OAuth popup flow for connection authorization.
 * Based on LogicAppsUX patterns from oAuth.ts utilities.
 */

// =============================================================================
// TYPES
// =============================================================================

/**
 * Parameters for initiating OAuth flow
 */
export interface OAuthFlowParams {
  /** Subscription ID */
  subscriptionId: string;
  /** Resource group */
  resourceGroup: string;
  /** Connection name (must exist already) */
  connectionName: string;
  /** Redirect URL after authorization */
  redirectUrl: string;
  /** Azure AD object ID */
  objectId: string;
  /** Azure AD tenant ID */
  tenantId: string;
  /** Access token for API calls */
  accessToken: string;
  /** Optional popup window options */
  popupOptions?: PopupOptions;
}

/**
 * Popup window configuration
 */
export interface PopupOptions {
  /** Window width */
  width?: number;
  /** Window height */
  height?: number;
  /** Window name */
  name?: string;
}

/**
 * OAuth error codes (standard OAuth 2.0 error codes)
 */
export type OAuthErrorCode =
  | 'access_denied'
  | 'invalid_request'
  | 'unauthorized_client'
  | 'unsupported_response_type'
  | 'invalid_scope'
  | 'server_error'
  | 'temporarily_unavailable'
  | 'timeout'
  | 'popup_blocked'
  | 'cancelled'
  | 'state_mismatch'
  | 'unknown';

/**
 * Result of OAuth flow
 */
export interface OAuthResult {
  /** Whether the flow was successful */
  success: boolean;
  /** Authorization code (if successful) */
  code?: string;
  /** Error message (if failed) */
  error?: string;
  /** Error code for programmatic handling */
  errorCode?: OAuthErrorCode;
  /** Whether the user cancelled */
  cancelled?: boolean;
}

/**
 * OAuth callback event data
 */
export interface OAuthCallbackData {
  /** Authorization code from OAuth provider */
  code?: string;
  /** Error from OAuth provider */
  error?: string;
  /** Error description */
  error_description?: string;
  /** State parameter for CSRF protection */
  state?: string;
}

// =============================================================================
// CONSTANTS
// =============================================================================

const DEFAULT_POPUP_WIDTH = 500;
const DEFAULT_POPUP_HEIGHT = 600;
const POPUP_NAME = 'oauth-popup';

// Timeout for waiting for OAuth callback (5 minutes)
const OAUTH_TIMEOUT_MS = 5 * 60 * 1000;

// Warning threshold before timeout (4 minutes) - exported for UI components
export const OAUTH_TIMEOUT_WARNING_MS = 4 * 60 * 1000;

// Poll interval for checking popup status
const POPUP_POLL_INTERVAL_MS = 500;

/**
 * Map OAuth error codes to user-friendly messages
 * Based on standard OAuth 2.0 error codes
 */
export function getOAuthErrorMessage(errorCode: OAuthErrorCode | string, errorDescription?: string): string {
  // Use error description if it's informative
  if (errorDescription && errorDescription.length > 10 && !errorDescription.includes('AADSTS')) {
    return errorDescription;
  }

  // Map standard OAuth error codes to friendly messages
  switch (errorCode) {
    case 'access_denied':
      return 'You declined the authorization request. Click "Try Again" to authorize.';
    case 'invalid_request':
      return 'There was a problem with the authorization request. Please try again or contact support if the issue persists.';
    case 'unauthorized_client':
      return 'This application is not authorized to request this permission. Please contact your administrator.';
    case 'unsupported_response_type':
      return 'The authorization server does not support this request type.';
    case 'invalid_scope':
      return 'The requested permissions are invalid. Please try again or contact support.';
    case 'server_error':
      return 'The authorization server encountered an error. Please try again later.';
    case 'temporarily_unavailable':
      return 'The authorization service is temporarily unavailable. Please try again in a few minutes.';
    case 'timeout':
      return 'Authorization timed out. Please click "Try Again" to complete the authorization.';
    case 'popup_blocked':
      return 'The authorization popup was blocked. Please allow popups for this site and try again.';
    case 'cancelled':
      return 'Authorization was cancelled. Click "Try Again" to continue.';
    case 'state_mismatch':
      return 'Security validation failed. Please try again.';
    default:
      // Try to extract meaningful info from Azure AD error codes
      if (errorDescription?.includes('AADSTS')) {
        const aadMatch = errorDescription.match(/AADSTS\d+/);
        const code = aadMatch?.[0];
        if (code) {
          return `Authorization failed (${code}). Please try again or use a different account.`;
        }
      }
      return errorDescription ?? 'Authorization failed. Please try again.';
  }
}

/**
 * Extract error code from OAuth error string
 */
export function extractOAuthErrorCode(error: string): OAuthErrorCode {
  const normalizedError = error.toLowerCase();

  if (normalizedError.includes('access_denied') || normalizedError.includes('user_cancelled')) {
    return 'access_denied';
  }
  if (normalizedError.includes('invalid_request')) {
    return 'invalid_request';
  }
  if (normalizedError.includes('unauthorized_client')) {
    return 'unauthorized_client';
  }
  if (normalizedError.includes('server_error')) {
    return 'server_error';
  }
  if (normalizedError.includes('temporarily_unavailable')) {
    return 'temporarily_unavailable';
  }
  if (normalizedError.includes('timeout') || normalizedError.includes('timed out')) {
    return 'timeout';
  }
  if (normalizedError.includes('popup') || normalizedError.includes('blocked')) {
    return 'popup_blocked';
  }
  if (normalizedError.includes('cancelled') || normalizedError.includes('canceled')) {
    return 'cancelled';
  }
  if (normalizedError.includes('state')) {
    return 'state_mismatch';
  }

  return 'unknown';
}

// =============================================================================
// OAUTH SERVICE CLASS
// =============================================================================

/**
 * Generate a cryptographically secure state parameter for CSRF protection
 */
function generateStateParameter(): string {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
}

/**
 * OAuth Service for managing connection authorization flows
 */
export class OAuthService {
  private currentPopup: Window | null = null;
  private messageHandler: ((event: MessageEvent) => void) | null = null;
  private resolveCallback: ((result: OAuthResult) => void) | null = null;
  private expectedOrigin: string;
  private expectedState: string | null = null;

  constructor() {
    this.expectedOrigin = window.location.origin;
  }

  /**
   * Initiate OAuth flow for a connection
   */
  async initiateOAuthFlow(params: OAuthFlowParams): Promise<OAuthResult> {
    // Clean up any previous flow
    this.cleanup();

    try {
      // Get consent URL from Azure
      const consentInfo = {
        firstPartyLoginUri: `https://login.microsoftonline.com/${params.tenantId}/oauth2/v2.0/authorize?client_id=00000002-0000-0000-c000-000000000000&response_type=code&redirect_uri=${encodeURIComponent(params.redirectUrl)}&response_mode=query&scope=openid%20offline_access%20https://management.azure.com/user_impersonation&state=12345`,
        consentLink: `https://login.microsoftonline.com/${params.tenantId}/oauth2/v2.0/authorize?client_id=00000002-0000-0000-c000-000000000000&response_type=code&redirect_uri=${encodeURIComponent(params.redirectUrl)}&response_mode=query&scope=openid%20offline_access%20https://management.azure.com/user_impersonation&state=12345`,
      };

      // Generate state parameter for CSRF protection
      this.expectedState = generateStateParameter();

      // Use first-party login URI if available (better UX), otherwise regular consent link
      let consentUrl = consentInfo.firstPartyLoginUri ?? consentInfo.consentLink;

      // Append state parameter to the consent URL for CSRF protection
      const urlObj = new URL(consentUrl);
      urlObj.searchParams.set('state', this.expectedState);
      consentUrl = urlObj.toString();

      // Open popup window
      const popup = this.openPopup(consentUrl, params.popupOptions);

      if (!popup) {
        return {
          success: false,
          error: getOAuthErrorMessage('popup_blocked'),
          errorCode: 'popup_blocked',
        };
      }

      this.currentPopup = popup;

      // Wait for OAuth callback
      const result = await this.waitForOAuthCallback(params);

      // If successful, confirm the consent code
      if (result.success && result.code) {
        // TODO: do nothing for now
      }

      return result;
    } catch (error) {
      const err = error as Error;
      const errorCode = extractOAuthErrorCode(err.message || '');
      return {
        success: false,
        error: getOAuthErrorMessage(errorCode, err.message),
        errorCode,
      };
    } finally {
      this.cleanup();
    }
  }

  /**
   * Open popup window for OAuth
   */
  private openPopup(url: string, options?: PopupOptions): Window | null {
    const width = options?.width ?? DEFAULT_POPUP_WIDTH;
    const height = options?.height ?? DEFAULT_POPUP_HEIGHT;
    const name = options?.name ?? POPUP_NAME;

    // Calculate center position
    const left = window.screenX + (window.innerWidth - width) / 2;
    const top = window.screenY + (window.innerHeight - height) / 2;

    const features = [
      `width=${width}`,
      `height=${height}`,
      `left=${left}`,
      `top=${top}`,
      'menubar=no',
      'toolbar=no',
      'location=yes',
      'status=yes',
      'resizable=yes',
      'scrollbars=yes',
    ].join(',');

    return window.open(url, name, features);
  }

  /**
   * Wait for OAuth callback via URL polling, postMessage, or popup close detection
   */
  private waitForOAuthCallback(params: OAuthFlowParams): Promise<OAuthResult> {
    return new Promise((resolve) => {
      this.resolveCallback = resolve;
      let resolved = false;

      const resolveOnce = (result: OAuthResult) => {
        if (!resolved) {
          resolved = true;
          resolve(result);
        }
      };

      // Set up message handler (backup for postMessage-based callbacks)
      this.messageHandler = (event: MessageEvent) => {
        this.handleOAuthMessage(event, params.redirectUrl);
      };
      window.addEventListener('message', this.messageHandler);

      // Set up timeout
      const timeoutId = setTimeout(() => {
        resolveOnce({
          success: false,
          error: getOAuthErrorMessage('timeout'),
          errorCode: 'timeout',
        });
        this.cleanup();
      }, OAUTH_TIMEOUT_MS);

      // Poll for popup URL or close
      // This approach directly reads the popup URL when it navigates back to our origin
      const pollInterval = setInterval(() => {
        if (!this.currentPopup) {
          clearInterval(pollInterval);
          clearTimeout(timeoutId);
          resolveOnce({
            success: false,
            cancelled: true,
            error: getOAuthErrorMessage('cancelled'),
            errorCode: 'cancelled',
          });
          return;
        }

        try {
          // Check if popup is closed
          if (this.currentPopup.closed) {
            clearInterval(pollInterval);
            clearTimeout(timeoutId);
            resolveOnce({
              success: false,
              cancelled: true,
              error: getOAuthErrorMessage('cancelled'),
              errorCode: 'cancelled',
            });
            return;
          }

          // Try to read popup URL - this will throw if cross-origin (expected while on MS login)
          const popupUrl = this.currentPopup.location.href;
          
          // Check if we're back on our origin with a code
          if (popupUrl.includes(window.location.origin)) {
            const urlObj = new URL(popupUrl);
            const code = urlObj.searchParams.get('code');
            const state = urlObj.searchParams.get('state');
            const error = urlObj.searchParams.get('error');
            const errorDescription = urlObj.searchParams.get('error_description');

            // Validate state parameter for CSRF protection
            if (state && this.expectedState && state !== this.expectedState) {
              console.error('[OAuthService] State mismatch - possible CSRF attack');
              clearInterval(pollInterval);
              clearTimeout(timeoutId);
              this.currentPopup.close();
              resolveOnce({
                success: false,
                error: getOAuthErrorMessage('state_mismatch'),
                errorCode: 'state_mismatch',
              });
              return;
            }

            if (code) {
              console.log('[OAuthService] Got authorization code from popup URL');
              clearInterval(pollInterval);
              clearTimeout(timeoutId);
              this.currentPopup.close();
              resolveOnce({
                success: true,
                code,
              });
              return;
            }

            if (error) {
              console.log('[OAuthService] Got error from popup URL:', error);
              clearInterval(pollInterval);
              clearTimeout(timeoutId);
              this.currentPopup.close();
              const errorCode = extractOAuthErrorCode(error);
              resolveOnce({
                success: false,
                error: getOAuthErrorMessage(errorCode, errorDescription ?? undefined),
                errorCode,
              });
              return;
            }
          }
        } catch {
          // Cross-origin error is expected while user is on OAuth page - ignore
          // Also handles COOP blocking access
        }
      }, POPUP_POLL_INTERVAL_MS);
    });
  }

  /**
   * Handle OAuth postMessage event
   */
  private handleOAuthMessage(event: MessageEvent, redirectUrl: string): void {
    // Verify origin - accept from our origin or common OAuth providers
    const allowedOrigins = [
      this.expectedOrigin,
      'https://login.microsoftonline.com',
      'https://login.live.com',
      new URL(redirectUrl).origin,
    ];

    if (!allowedOrigins.includes(event.origin)) {
      return;
    }

    const data = event.data as OAuthCallbackData | string;

    // Handle different message formats
    let callbackData: OAuthCallbackData;

    if (typeof data === 'string') {
      // Try to parse as URL params
      try {
        const params = new URLSearchParams(data);
        const code = params.get('code');
        const state = params.get('state');
        const error = params.get('error');
        const errorDesc = params.get('error_description');

        callbackData = {};
        if (code) callbackData.code = code;
        if (state) callbackData.state = state;
        if (error) callbackData.error = error;
        if (errorDesc) callbackData.error_description = errorDesc;
      } catch {
        return; // Not a valid message
      }
    } else if (typeof data === 'object' && data !== null) {
      callbackData = data;
    } else {
      return;
    }

    // Validate state parameter if present in callback (CSRF protection)
    if (callbackData.state && this.expectedState) {
      if (callbackData.state !== this.expectedState) {
        this.resolveCallback?.({
          success: false,
          error: getOAuthErrorMessage('state_mismatch'),
          errorCode: 'state_mismatch',
        });
        this.resolveCallback = null;
        this.cleanup();
        return;
      }
    }

    // Check for authorization code
    if (callbackData.code) {
      this.resolveCallback?.({
        success: true,
        code: callbackData.code,
      });
      this.resolveCallback = null;
      this.cleanup();
      return;
    }

    // Check for error
    if (callbackData.error) {
      const errorCode = extractOAuthErrorCode(callbackData.error);
      this.resolveCallback?.({
        success: false,
        error: getOAuthErrorMessage(errorCode, callbackData.error_description),
        errorCode,
      });
      this.resolveCallback = null;
      this.cleanup();
    }
  }

  /**
   * Clean up OAuth flow resources
   */
  private cleanup(): void {
    // Close popup if open
    if (this.currentPopup && !this.currentPopup.closed) {
      this.currentPopup.close();
    }
    this.currentPopup = null;

    // Remove message handler
    if (this.messageHandler) {
      window.removeEventListener('message', this.messageHandler);
      this.messageHandler = null;
    }

    this.resolveCallback = null;
    this.expectedState = null;
  }

  /**
   * Cancel any ongoing OAuth flow
   */
  cancel(): void {
    if (this.resolveCallback) {
      this.resolveCallback({
        success: false,
        cancelled: true,
        error: getOAuthErrorMessage('cancelled'),
        errorCode: 'cancelled',
      });
    }
    this.cleanup();
  }
}

// =============================================================================
// SINGLETON INSTANCE
// =============================================================================

let oauthServiceInstance: OAuthService | null = null;

/**
 * Get the OAuth service singleton
 */
export function getOAuthService(): OAuthService {
  if (!oauthServiceInstance) {
    oauthServiceInstance = new OAuthService();
  }
  return oauthServiceInstance;
}

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Parse OAuth callback from URL
 */
export function parseOAuthCallback(url: string): OAuthCallbackData | null {
  try {
    const urlObj = new URL(url);
    const params = new URLSearchParams(urlObj.search);
    const hashParams = new URLSearchParams(urlObj.hash.slice(1));

    // Check both query params and hash params
    const code = params.get('code') ?? hashParams.get('code');
    const error = params.get('error') ?? hashParams.get('error');

    if (!code && !error) {
      return null;
    }

    const result: OAuthCallbackData = {};
    if (code) result.code = code;
    if (error) result.error = error;

    const errorDesc = params.get('error_description') ?? hashParams.get('error_description');
    if (errorDesc) result.error_description = errorDesc;

    const state = params.get('state') ?? hashParams.get('state');
    if (state) result.state = state;

    return result;
  } catch {
    return null;
  }
}

/**
 * Build redirect URL for OAuth callback
 */
export function buildOAuthRedirectUrl(): string {
  // Use current origin with a dedicated callback path
  return `${window.location.origin}/oauth/callback`;
}

/**
 * Check if a URL is an OAuth callback URL
 */
export function isOAuthCallbackUrl(url: string): boolean {
  try {
    const urlObj = new URL(url);
    return (
      urlObj.pathname === '/oauth/callback' ||
      urlObj.searchParams.has('code') ||
      urlObj.hash.includes('code=')
    );
  } catch {
    return false;
  }
}
